import { gradeCalculationKeys } from '@/composables/gradeCalculation/gradeCalculation.keys';
import { appearanceToDto } from '@/core/entity-appearance/appearanceFromDto';
import {
  archiveSchoolClass as apiArchiveSchoolClass,
  createSchoolClass as apiCreateSchoolClass,
  createSchoolClassSetup as apiCreateSchoolClassSetup,
  createSchoolClassSetupWithImage,
  deleteSchoolClass as apiDeleteSchoolClass,
  dismissSchoolYearOverBanner,
  fetchSchoolClasses,
  setBaseClass as apiSetBaseClass,
  updateSchoolClass as apiUpdateSchoolClass,
} from '@/api/resources/schoolClass.api';
import type { TEntityAppearance } from '@/core/entity-appearance/entityAppearance.types';
import type { Components } from '@/api/generated/api';
import { EntityAppearanceType, SchoolClassType } from '@/api/generated/api.enum';
import { useAuth } from '@/auth/useAuth';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { IOnboardingStudent } from '@/interfaces';
import { resolveContainerKey } from '@/schoolClasses/containerTranslationKeys';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { useQueryClient } from '@tanstack/vue-query';
import { EIcon } from 'common/enum/misc.enum';
import { getClassName } from 'common/utils/format.utils';
import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplayName } from './useDisplayName';
import { useWhoAmI } from './useWhoAmI';
import { useCurrentClassStore } from '@/stores/useCurrentClassStore';
import { storeToRefs } from 'pinia';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { schoolClassAccessKeys } from './schoolClassAccess/schoolClassAccess.keys';
import { useRefreshSetupChecklist } from './setupChecklist/useRefreshSetupChecklist';
import { invalidateObservationTimelineQueries } from './observationTimeline/observationTimelineQueryInvalidation';
import { supportMeasureEntryKeys } from './supportMeasureEntry/supportMeasureEntry.keys';
import { useStudentAvatarUploads } from './useStudentAvatarUploads';
import { applyCreatedStudents, ensureStudentClientKey } from '@/utils/students.utils';
import { schoolClassKeys } from './schoolClass.keys';

export interface ICreateOrUpdateSchoolClass {
  name: string;
  nickname: string;
  schoolLevel?: number;
  schoolYearId?: number;
  isOnboardingFinalized?: boolean;
  isAssessmentTypeOnboardingFinalized?: boolean;
  isStudentOnboardingFinalized?: boolean;
  isFullyOnboarded?: boolean;
  baseClassId?: number | null;
  type?: Components.Schemas.SchoolClassType;
}

export interface ICreateSchoolClassSetup {
  appearance?: TEntityAppearance;
  onCreated: (schoolClass: Components.Schemas.SchoolClassDto) => void;
  createSchoolClassDto: ICreateOrUpdateSchoolClass;
  schoolSubjectIdsToCopy: number[];
  accessRights: Components.Schemas.AddAccessRightDto[];
  invitationAccessRights: Components.Schemas.AddInvitationAccessRightDto[];
  studentsToCreate: IOnboardingStudent[];
  existingStudentIds: number[];
}

interface ISchoolClassesComposable {
  archiveSchoolClass: (schoolClassId: number, finalNote: string) => Promise<void>;
  archiveSchoolClassesBulk: (
    classesToArchive: { schoolClassId: number; finalNote: string }[],
  ) => Promise<{ archivedClassIds: number[]; failedClassIds: number[] }>;
  addSchoolClass: (data: ICreateOrUpdateSchoolClass) => Promise<Components.Schemas.SchoolClassDto>;
  createSchoolClassSetup: (data: ICreateSchoolClassSetup) => Promise<Components.Schemas.SchoolClassDto>;
  createSchoolClass: (data: ICreateOrUpdateSchoolClass) => Promise<Components.Schemas.SchoolClassDto>;
  deleteSchoolClassesBulk: (classesOrGroups: Components.Schemas.SchoolClassDto[]) => Promise<boolean>;
  deleteSchoolClass: (
    schoolClass: Components.Schemas.SchoolClassDto,
    navigateAfterDelete?: () => void | Promise<void>,
  ) => Promise<boolean>;
  refresh: () => Promise<void>;
  classesOrGroups: Ref<Components.Schemas.SchoolClassDto[] | undefined>;
  classes: Ref<Components.Schemas.SchoolClassDto[]>;
  classesNotArchived: Ref<Components.Schemas.SchoolClassDto[]>;
  groups: Ref<Components.Schemas.SchoolClassDto[]>;
  classesOrGroupsNotArchived: Ref<Components.Schemas.SchoolClassDto[]>;
  areClassesOrGroupsPending: Ref<boolean>;
  isLoading: Ref<boolean>;
  updateSchoolClass: (schoolClassId: number, data: ICreateOrUpdateSchoolClass, showToast?: boolean) => Promise<void>;
  dismissSchoolOverBanner: (schoolClassId: number, showToast?: boolean) => Promise<void>;
  setBaseClass: (dto: Components.Schemas.SetBaseClassDto, schoolClassId: number) => Promise<void>;
}

export function useSchoolClasses(): ISchoolClassesComposable {
  const { createSuccessToast, editSuccessToast, deleteSuccessToast, catchError } = useToast();
  const currentClassStore = useCurrentClassStore();
  const { currentClass } = storeToRefs(currentClassStore);
  const { isLoggedIn } = useAuth();
  const { confirm } = useConfirmDialog();
  const { t } = useI18n();
  const { whoAmI } = useWhoAmI();
  const { getUserDisplayName } = useDisplayName();
  const trialUpsellStore = useTrialUpsellStore();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();
  const { trackEvent } = useAnalytics();
  const { uploadStudentAvatarPictures } = useStudentAvatarUploads();

  const getClassesOrGroups = async (): Promise<Components.Schemas.SchoolClassDto[]> => {
    if (!whoAmI.value?.currentTenantId) return [];
    return fetchSchoolClasses();
  };

  const schoolClassesListQueryKey = computed(() => schoolClassKeys.list(whoAmI.value?.currentTenantId));
  const canLoadSchoolClasses = computed(() => isLoggedIn.value && !!whoAmI.value?.currentTenantId);
  const queryClient = useQueryClient();
  const {
    data: classesOrGroups,
    isFetching: areClassesOrGroupsFetching,
    isLoading: areClassesOrGroupsLoading,
    isPending: areClassesOrGroupsPending,
  } = useQueryWithToast({
    queryKey: schoolClassesListQueryKey,
    queryFn: getClassesOrGroups,
    enabled: canLoadSchoolClasses,
  });
  const isLoading = computed(() => areClassesOrGroupsLoading.value || areClassesOrGroupsFetching.value);

  const classesOrGroupsNotArchived = computed(() => {
    return classesOrGroups.value?.filter((schoolClass) => !schoolClass.archivedAt) ?? [];
  });

  const classes = computed(() => classesOrGroups.value?.filter((c) => c.type !== SchoolClassType.group) ?? []);
  // growth prompts and the setup checklist talk about classes only, so a group must not satisfy them
  const classesNotArchived = computed(() => classes.value.filter((schoolClass) => !schoolClass.archivedAt));
  const groups = computed(() => classesOrGroups.value?.filter((c) => c.type === SchoolClassType.group) ?? []);

  const toCreateSchoolClassDto = (data: ICreateOrUpdateSchoolClass): Components.Schemas.CreateSchoolClassDto => {
    assertNotNullOrUndefined(data.schoolYearId, 'schoolYearId is required to create a class');

    return {
      name: data.name,
      isActive: true,
      isTemplate: false,
      schoolLevel: data.schoolLevel ?? 1,
      nickname: data.nickname,
      schoolYearId: data.schoolYearId,
      ...(data.type !== undefined ? { type: data.type } : {}),
      ...(data.isFullyOnboarded !== undefined ? { isFullyOnboarded: data.isFullyOnboarded } : {}),
      ...(data.baseClassId !== undefined ? { baseClassId: data.baseClassId } : {}),
    };
  };

  async function createSchoolClass(data: ICreateOrUpdateSchoolClass): Promise<Components.Schemas.SchoolClassDto> {
    const dto = toCreateSchoolClassDto(data);
    const created = await apiCreateSchoolClass(dto);
    trackEvent(AnalyticsEvents.schoolClass.created, { class_type: created.type, creation_flow: 'simple' });
    await queryClient.invalidateQueries({ queryKey: schoolClassAccessKeys.accessRightsList() });
    await refreshSetupChecklist();
    if (created.type !== SchoolClassType.group) trialUpsellStore.recordClassesCreated();
    trialUpsellStore.requestEligibilityCheck();
    return created;
  }

  async function createSchoolClassSetup(data: ICreateSchoolClassSetup): Promise<Components.Schemas.SchoolClassDto> {
    const dto: Components.Schemas.CreateSchoolClassSetupDto = {
      appearance:
        data.appearance && data.appearance.type !== EntityAppearanceType.image
          ? appearanceToDto(data.appearance)
          : undefined,
      schoolClass: toCreateSchoolClassDto(data.createSchoolClassDto),
      schoolSubjectIdsToCopy: data.schoolSubjectIdsToCopy,
      accessRights: data.accessRights,
      invitationAccessRights: data.invitationAccessRights,
      studentsToCreate: data.studentsToCreate.map((student) => ({
        clientKey: ensureStudentClientKey(student),
        firstName: student.firstName,
        lastName: student.lastName,
        avatar: student.avatar,
      })),
      existingStudentIds: data.existingStudentIds,
    };
    let created: Components.Schemas.SchoolClassSetupResponseDto;
    if (data.appearance?.type === EntityAppearanceType.image && data.appearance.source instanceof File) {
      created = await createSchoolClassSetupWithImage({
        dto,
        file: data.appearance.source,
      });
    } else {
      // Existing image URLs come from a predecessor; the server copies its file reference.
      if (data.appearance?.type === EntityAppearanceType.image) {
        assertNotNullOrUndefined(data.createSchoolClassDto.baseClassId);
      }
      created = await apiCreateSchoolClassSetup(dto);
    }
    trackEvent(AnalyticsEvents.schoolClass.created, { class_type: created.data.type, creation_flow: 'setup' });
    data.onCreated(created.data);
    applyCreatedStudents(data.studentsToCreate, created.createdStudents);
    await uploadStudentAvatarPictures(data.studentsToCreate);
    await queryClient.invalidateQueries({ queryKey: ['students'] });
    await queryClient.invalidateQueries({ queryKey: schoolClassAccessKeys.accessRightsList() });
    await refreshSetupChecklist();
    // the upsell counts classes; a group is a container of its own
    if (created.data.type !== SchoolClassType.group) trialUpsellStore.recordClassesCreated();
    trialUpsellStore.recordStudentsCreatedOrLinked(data.studentsToCreate.length + data.existingStudentIds.length);
    if (created.data.isOnboardingFinalized) {
      trialUpsellStore.requestEligibilityCheck();
    }
    return created.data;
  }

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: schoolClassesListQueryKey });
  };

  async function addSchoolClass(data: ICreateOrUpdateSchoolClass): Promise<Components.Schemas.SchoolClassDto> {
    const result = await catchError(async () => {
      const created = await createSchoolClass(data);
      await refresh();
      createSuccessToast();
      return created;
    });
    return result;
  }

  async function deleteSchoolClass(
    schoolClass: Components.Schemas.SchoolClassDto,
    navigateAfterDelete?: () => void | Promise<void>,
  ): Promise<boolean> {
    const confirmInputCheckValues = [whoAmI.value ? getUserDisplayName(whoAmI.value) : '', whoAmI.value?.email].filter(
      (value): value is string => !!value,
    );
    const isGroup = schoolClass.type === SchoolClassType.group;
    const confirmed = await confirm({
      title: t(resolveContainerKey('remove', isGroup)),
      description: t(resolveContainerKey('removeConfirmText', isGroup), { className: getClassName(schoolClass) }),
      confirmButtonLabel: t(resolveContainerKey('delete', isGroup)),
      declineButtonLabel: t('general.cancel'),
      theme: 'danger',
      size: 'lg',
      confirmButtonIcon: EIcon.DELETE,
      hint: t(resolveContainerKey('removeConfirmHint', isGroup), { className: schoolClass.name }),
      checkInput: {
        inputPlaceholder: t('classes.removeConfirmInputPlaceholder'),
        inputCheckValue: confirmInputCheckValues,
      },
    });

    if (!confirmed) return false;

    let didDelete = false;

    await catchError(
      async () => {
        await apiDeleteSchoolClass({ schoolClassId: schoolClass.id });
        await queryClient.invalidateQueries({
          queryKey: gradeCalculationKeys.scaleUsage(schoolClass.tenantId ?? undefined),
        });

        if (currentClass.value?.id === schoolClass.id) {
          currentClassStore.setCurrentClass(null);
        }

        await refresh();
        await refreshSetupChecklist();

        if (navigateAfterDelete) {
          await navigateAfterDelete();
        }

        deleteSuccessToast();
        didDelete = true;
      },
      { CLASS_HAS_STILL_STUDENTS: resolveContainerKey('deleteBlockedByStudents', isGroup) },
    );

    return didDelete;
  }

  async function deleteSchoolClassesBulk(
    classesOrGroupsToDelete: Components.Schemas.SchoolClassDto[],
  ): Promise<boolean> {
    if (!classesOrGroupsToDelete.length) return false;

    const confirmInputCheckValues = [whoAmI.value ? getUserDisplayName(whoAmI.value) : '', whoAmI.value?.email].filter(
      (value): value is string => !!value,
    );

    const classNames = classesOrGroupsToDelete.map((schoolClass) => getClassName(schoolClass)).join(', ');
    const isMultiDelete = classesOrGroupsToDelete.length > 1;

    const confirmed = await confirm({
      title: t(isMultiDelete ? 'classes.removeBulk' : 'classes.remove'),
      description: t('classes.removeBulkConfirmText', { count: classesOrGroupsToDelete.length, classNames }),
      confirmButtonLabel: t(isMultiDelete ? 'classes.removeBulk' : 'classes.delete'),
      declineButtonLabel: t('general.cancel'),
      theme: 'danger',
      size: 'lg',
      confirmButtonIcon: EIcon.DELETE,
      hint: t('classes.removeBulkConfirmHint', { count: classesOrGroupsToDelete.length }),
      checkInput: {
        inputPlaceholder: t('classes.removeConfirmInputPlaceholder'),
        inputCheckValue: confirmInputCheckValues,
      },
    });

    if (!confirmed) return false;

    await catchError(async () => {
      for (const schoolClass of classesOrGroupsToDelete) {
        await apiDeleteSchoolClass({ schoolClassId: schoolClass.id });
        await queryClient.invalidateQueries({
          queryKey: gradeCalculationKeys.scaleUsage(schoolClass.tenantId ?? undefined),
        });

        if (currentClass.value?.id === schoolClass.id) {
          currentClassStore.setCurrentClass(null);
        }
      }
      await refresh();
      await refreshSetupChecklist();
      deleteSuccessToast();
    });

    return true;
  }

  async function updateSchoolClass(
    schoolClassId: number,
    data: ICreateOrUpdateSchoolClass,
    showToast: boolean = true,
  ): Promise<void> {
    await catchError(async () => {
      await apiUpdateSchoolClass({ schoolClassId, dto: data });
      await refresh();
      if (showToast) editSuccessToast();
    });
  }

  async function dismissSchoolOverBanner(schoolClassId: number, showToast: boolean = false): Promise<void> {
    await catchError(async () => {
      await dismissSchoolYearOverBanner({ schoolClassId });
      await refresh();
      if (showToast) editSuccessToast();
    });
  }

  const invalidateCompletedSupportMeasures = async (schoolClassIds: number[]): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: supportMeasureEntryKeys.all }),
      ...schoolClassIds.map((schoolClassId) => invalidateObservationTimelineQueries(queryClient, schoolClassId)),
    ]);
  };

  async function archiveSchoolClass(schoolClassId: number, finalNote: string): Promise<void> {
    await catchError(async () => {
      await apiArchiveSchoolClass({ schoolClassId, finalNote });
      await refresh();
      await refreshSetupChecklist();
      await invalidateCompletedSupportMeasures([schoolClassId]);
      editSuccessToast();
    });
  }

  async function archiveSchoolClassesBulk(
    classesToArchive: { schoolClassId: number; finalNote: string }[],
  ): Promise<{ archivedClassIds: number[]; failedClassIds: number[] }> {
    const archivedClassIds: number[] = [];
    const failedClassIds: number[] = [];

    for (const { schoolClassId, finalNote } of classesToArchive) {
      try {
        await apiArchiveSchoolClass({ schoolClassId, finalNote });
        archivedClassIds.push(schoolClassId);
      } catch {
        failedClassIds.push(schoolClassId);
      }
    }

    await refresh();
    await refreshSetupChecklist();
    await invalidateCompletedSupportMeasures(archivedClassIds);

    return { archivedClassIds, failedClassIds };
  }

  async function setBaseClass(dto: Components.Schemas.SetBaseClassDto, schoolClassId: number): Promise<void> {
    await apiSetBaseClass({ schoolClassId, dto });
  }

  return {
    addSchoolClass,
    archiveSchoolClass,
    archiveSchoolClassesBulk,
    areClassesOrGroupsPending,
    classes,
    classesNotArchived,
    createSchoolClassSetup,
    createSchoolClass,
    deleteSchoolClassesBulk,
    deleteSchoolClass,
    dismissSchoolOverBanner,
    groups,
    isLoading,
    refresh,
    classesOrGroups,
    classesOrGroupsNotArchived,
    setBaseClass,
    updateSchoolClass,
  };
}
